import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdCloseFullscreen, MdLogout, MdMic, MdMicOff, MdOpenInFull, MdVideocam, MdVideocamOff } from 'react-icons/md';
import { useVoice } from '../hooks/useVoice';
import { getPersonId, getPersonName } from '../utils/messengerPeopleUtils';
import { Avatar } from './Avatar';
import { VideoTile } from './VideoTile';
import styles from './VoiceCallOverlay.module.css';

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

function ParticipantTile({ id, label, videoStream, isMuted, isPinned, isLarge, fill, isConnecting, onClick }) {
  const wrapperClass = isLarge
    ? styles.videoWrapperLarge
    : fill
      ? styles.videoWrapperFill
      : styles.videoWrapper;

  return (
    <div
      className={`${styles.participant} ${isLarge ? styles.participantLarge : ''} ${isPinned ? styles.participantPinned : ''}`}
      onClick={onClick}
      title={isPinned ? 'Click to unpin' : 'Click to pin'}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {videoStream ? (
        <div className={wrapperClass}>
          <VideoTile stream={videoStream} isLocal={id === 'self'} isMuted={isMuted} />
        </div>
      ) : (
        <div className={styles.avatarWrapper}>
          <Avatar label={label} />
        </div>
      )}
      <span className={styles.participantName}>{isConnecting ? 'Connecting…' : label}</span>
    </div>
  );
}

export function VoiceCallOverlay() {
  const voice = useVoice() || {};
  const {
    callStatus,
    activeRoom,
    isMuted,
    isCreator,
    cameraOn,
    localVideoStreamRef,
    remoteVideoStreams,
    connectingPeers,
    endCall,
    leaveCall,
    toggleMute,
    toggleCamera,
  } = voice;
  const resolvedCallStatus = callStatus || 'idle';
  const resolvedIsMuted = Boolean(isMuted);
  const resolvedCameraOn = Boolean(cameraOn);
  const resolvedLocalVideoStreamRef = localVideoStreamRef || { current: null };
  const resolvedRemoteVideoStreams = remoteVideoStreams || new Map();
  const resolvedConnectingPeers = connectingPeers || new Set();

  const [pinnedId, setPinnedId] = useState(null);
  const autoPinnedIdRef = useRef(null);

  const participants = activeRoom?.participants ?? [];
  const roomName = activeRoom?.name || 'Ongoing Call';
  const shouldEndRoom = isCreator || typeof leaveCall !== 'function';
  const hangupAction =
    shouldEndRoom && typeof endCall === 'function' ? endCall : leaveCall || endCall;
  const hangupLabel = shouldEndRoom && typeof endCall === 'function' ? 'End call' : 'Leave call';

  const allParticipants = useMemo(() => [
    {
      id: 'self',
      label: 'You',
      videoStream: resolvedCameraOn ? resolvedLocalVideoStreamRef.current : null,
      isMuted: resolvedIsMuted,
      isConnecting: false,
    },
    ...participants.map((p) => {
      const participantId = getPersonId(p);
      return {
        id: participantId,
        label: getPersonName(p),
        videoStream: resolvedRemoteVideoStreams.get(participantId) ?? null,
        isMuted: false,
        isConnecting: resolvedConnectingPeers.has(participantId),
      };
    }),
  // localVideoStreamRef.current is gated on cameraOn, so cameraOn as dep is sufficient
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [
    participants,
    resolvedCameraOn,
    resolvedConnectingPeers,
    resolvedIsMuted,
    resolvedLocalVideoStreamRef,
    resolvedRemoteVideoStreams,
  ]);

  const [isMinimized, setIsMinimized] = useState(false);

  const toggleMinimize = useCallback(() => setIsMinimized((prev) => !prev), []);

  // Reset transient UI state when a call ends
  useEffect(() => {
    if (resolvedCallStatus === 'idle') {
      setPinnedId(null);
      autoPinnedIdRef.current = null;
      setIsMinimized(false);
    }
  }, [resolvedCallStatus]);

  // Timer — drives from server createdAt when available, falls back to local counter for ringing phase
  const [elapsed, setElapsed] = useState(0);
  const localPhaseStartRef = useRef(null);
  useEffect(() => {
    if (resolvedCallStatus === 'idle') {
      setElapsed(0);
      localPhaseStartRef.current = null;
      return;
    }
    const serverStart = activeRoom?.createdAt ? new Date(activeRoom.createdAt).getTime() : null;
    if (resolvedCallStatus === 'in-call' && serverStart) {
      const tick = () => setElapsed(Math.floor((Date.now() - serverStart) / 1000));
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }
    if (localPhaseStartRef.current === null) {
      localPhaseStartRef.current = Date.now();
      setElapsed(0);
    }
    const start = localPhaseStartRef.current;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [resolvedCallStatus, activeRoom?.createdAt]);

  // Auto-pin the sole video participant; auto-unpin when that condition no longer holds
  useEffect(() => {
    const withVideo = allParticipants.filter((p) => p.videoStream);
    if (withVideo.length === 1) {
      const soleId = withVideo[0].id;
      setPinnedId((prev) => {
        if (prev === null) {
          autoPinnedIdRef.current = soleId;
          return soleId;
        }
        return prev;
      });
    } else {
      setPinnedId((prev) => {
        if (prev !== null && prev === autoPinnedIdRef.current) {
          autoPinnedIdRef.current = null;
          return null;
        }
        return prev;
      });
    }
  }, [allParticipants]);

  if (resolvedCallStatus === 'idle') return null;

  const togglePin = (id) => {
    autoPinnedIdRef.current = null;
    setPinnedId((prev) => (prev === id ? null : id));
  };

  const pinnedParticipant = pinnedId ? allParticipants.find((p) => p.id === pinnedId) : null;
  const stripParticipants = pinnedParticipant ? allParticipants.filter((p) => p.id !== pinnedId) : [];

  // Minimized: slim banner
  if (isMinimized) {
    return (
      <div className={styles.banner} role="region" aria-label="Active voice call">
        <span className={styles.liveIndicator} aria-hidden="true" />
        <span className={styles.liveBadge}>Live</span>
        <span className={styles.roomName}>{roomName}</span>
        <span className={styles.timer}>{formatElapsed(elapsed)}</span>
        <div className={styles.bannerActions}>
          <button
            type="button"
            className={`${styles.actionBtn} ${resolvedIsMuted ? styles.mutedBtn : ''}`}
            onClick={toggleMute}
            aria-label={resolvedIsMuted ? 'Unmute' : 'Mute'}
            title={resolvedIsMuted ? 'Unmute' : 'Mute'}
          >
            {resolvedIsMuted ? <MdMicOff size={16} /> : <MdMic size={16} />}
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.leaveBtn}`}
            onClick={hangupAction}
            aria-label={hangupLabel}
            title={hangupLabel}
          >
            <MdLogout size={16} />
          </button>
          <button
            type="button"
            className={styles.windowBtn}
            onClick={toggleMinimize}
            aria-label="Expand call"
            title="Expand call"
          >
            <MdOpenInFull size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Expanded: near full-screen overlay
  return (
    <div className={styles.overlay} role="region" aria-label="Active voice call">
      <div className={styles.header}>
        <span className={styles.liveIndicator} aria-hidden="true" />
        <span className={styles.liveBadge}>Live</span>
        <span className={styles.roomName}>{roomName}</span>
        <span className={styles.timer}>{formatElapsed(elapsed)}</span>
        <button
          type="button"
          className={styles.windowBtn}
          onClick={toggleMinimize}
          aria-label="Minimize"
          title="Minimize"
        >
          <MdCloseFullscreen size={14} />
        </button>
      </div>

      {resolvedCallStatus === 'initiated' && (
        <p className={styles.connecting}>
          <span>Ringing...</span> <span>{formatElapsed(elapsed)}</span>
        </p>
      )}

      {resolvedCallStatus === 'connecting' && (
        <p className={styles.connecting}>
          <span>Connecting...</span> <span>{formatElapsed(elapsed)}</span>
        </p>
      )}

      {resolvedCallStatus === 'in-call' && (
        <>
          {pinnedParticipant ? (
            <div className={styles.pinnedLayout}>
              <ParticipantTile
                {...pinnedParticipant}
                isPinned
                isLarge
                onClick={() => togglePin(pinnedParticipant.id)}
              />
              {stripParticipants.length > 0 && (
                <div className={styles.strip}>
                  {stripParticipants.map((p) => (
                    <ParticipantTile
                      key={p.id}
                      {...p}
                      isPinned={false}
                      isLarge={false}
                      onClick={() => togglePin(p.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.participants}>
              {allParticipants.map((p) => (
                <ParticipantTile
                  key={p.id}
                  {...p}
                  isPinned={false}
                  isLarge={false}
                  fill
                  onClick={() => togglePin(p.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.actionBtn} ${resolvedIsMuted ? styles.mutedBtn : ''}`}
          onClick={toggleMute}
          aria-label={resolvedIsMuted ? 'Unmute' : 'Mute'}
          title={resolvedIsMuted ? 'Unmute' : 'Mute'}
        >
          {resolvedIsMuted ? <MdMicOff size={18} /> : <MdMic size={18} />}
        </button>

        <button
          type="button"
          className={`${styles.actionBtn} ${!resolvedCameraOn ? styles.mutedBtn : ''}`}
          onClick={toggleCamera}
          aria-label={resolvedCameraOn ? 'Turn off camera' : 'Turn on camera'}
          title={resolvedCameraOn ? 'Turn off camera' : 'Turn on camera'}
        >
          {resolvedCameraOn ? <MdVideocam size={18} /> : <MdVideocamOff size={18} />}
        </button>

        <button
          type="button"
          className={`${styles.actionBtn} ${styles.leaveBtn}`}
          onClick={hangupAction}
          aria-label={hangupLabel}
          title={hangupLabel}
        >
          <MdLogout size={18} />
        </button>
      </div>
    </div>
  );
}
